import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Palette, Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { playSound } from "@/lib/piggySound";
import { useLook } from "@/context/LookContext";
import { useLiveRefresh } from "@/lib/live";
import { Celebration } from "@/components/kids/kidsUi";
import GlassPiggy, { coinCount } from "@/components/piggy/GlassPiggy";
import StylePiggy from "@/components/piggy/StylePiggy";

const POLL_MS = 15000;
const MAX_FLY = 12;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Shows the celebration queue ("You can afford <item>!") once, then tells the server.
export function useAffordCelebrations(hubId, kidId, list) {
  const { look } = useLook();
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!list?.length) return;
    setItems((prev) => [...prev, ...list.filter((c) => !prev.some((p) => p.id === c.id))]);
    api.post(`/organizations/${hubId}/piggy-bank/${kidId}/celebrated`, { item_ids: list.map((c) => c.id) }).catch(() => {});
  }, [list, hubId, kidId]);
  const current = items[0];
  useEffect(() => { if (current) playSound("chaching", look?.sound_muted); }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = useCallback(() => setItems((prev) => prev.slice(1)), []);
  return current ? <Celebration key={current.id} big emoji="🎉" text={`You can afford ${current.name}!`} onDone={done} duration={3200} testid="afford-celebration" /> : null;
}

// Animated glass piggy for the signed-in kid: plays unseen ledger entries one at a time.
export default function PiggyStage({ hubId, kidId, size = "lg", poll = false, controls = false, onBank }) {
  const { look, update } = useLook();
  const muted = !!look?.sound_muted;
  const reduce = useReducedMotion();
  const [bank, setBank] = useState(null);
  const [bal, setBal] = useState(null);
  const [flying, setFlying] = useState(null);
  const [toast, setToast] = useState(null);
  const [wiggle, setWiggle] = useState(0);
  const [styling, setStyling] = useState(false);
  const [celebrations, setCelebrations] = useState(null);
  const [played, setPlayed] = useState(0);
  const st = useRef({ queue: [], seen: new Set(), running: false, bal: null, alive: true });
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const run = useCallback(async () => {
    const s = st.current;
    if (s.running) return;
    s.running = true;
    while (s.queue.length && s.alive) {
      const e = s.queue.shift();
      // Mark seen as the animation starts so a reload mid-drop never replays it.
      api.post(`/organizations/${hubId}/piggy-bank/${kidId}/seen`, { entry_id: e.id }).catch(() => {});
      const before = s.bal;
      const after = Math.max(0, +(before + e.amount).toFixed(2));
      const credit = e.amount >= 0;
      const k = Math.min(MAX_FLY, Math.max(1, Math.abs(coinCount(after) - coinCount(before))));
      setToast({ id: e.id, credit, text: `${credit ? "+" : "−"}${money(Math.abs(e.amount))} ${e.label}` });
      if (credit) {
        setFlying({ kind: "in", from: coinCount(before), k, key: e.id, base: coinCount(before) });
        for (let n = 0; n < k; n++) playSound("clink", mutedRef.current, 0.35 + n * 0.09);
      } else {
        setFlying({ kind: "out", from: Math.max(0, coinCount(before) - k), k, key: e.id, base: Math.max(0, coinCount(before) - k) });
        playSound("tumble", mutedRef.current, 0.2);
      }
      await wait(reduce ? 900 : 900 + k * 90);
      s.bal = after;
      setBal(after);
      setFlying(null);
      setPlayed((n) => n + 1);
      await wait(reduce ? 400 : 550);
    }
    s.running = false;
    if (s.pendingCel) { setCelebrations(s.pendingCel); s.pendingCel = null; }
    setTimeout(() => s.alive && setToast(null), 2600);
  }, [hubId, kidId, reduce]);

  const load = useCallback(async (first) => {
    const { data } = await api.get(`/organizations/${hubId}/piggy-bank/${kidId}`);
    const s = st.current;
    if (!s.alive) return;
    setBank(data);
    onBank?.(data);
    const fresh = data.unseen.filter((e) => !s.seen.has(e.id));
    fresh.forEach((e) => s.seen.add(e.id));
    if (first) {
      s.bal = Math.max(0, +(data.balance - fresh.reduce((t, e) => t + e.amount, 0)).toFixed(2));
      setBal(s.bal);
    }
    if (fresh.length) { s.queue.push(...fresh); await run(); } else if (!s.running && s.bal !== data.balance) { s.bal = data.balance; setBal(data.balance); }
    if (data.celebrations.length) {
      if (s.running) s.pendingCel = data.celebrations; else setCelebrations(data.celebrations);
    }
  }, [hubId, kidId, run, onBank]);

  useEffect(() => {
    const s = st.current;
    s.alive = true;
    load(true).catch(() => {});
    const id = poll ? setInterval(() => load(false).catch(() => {}), POLL_MS) : null;
    return () => { s.alive = false; clearInterval(id); };
  }, [load, poll]);
  useLiveRefresh(() => load(false).catch(() => {}), (m) => m.type === "piggy.ledger.added" && m.payload?.kid_id === kidId, null);

  const celebration = useAffordCelebrations(hubId, kidId, celebrations);
  if (!bank || bal === null) return <div className="aspect-[4/3] w-full max-w-[460px] animate-pulse rounded-3xl bg-q-raised" />;
  const count = flying ? flying.base : coinCount(bal);
  const tap = () => { setWiggle((w) => w + 1); playSound("oink", muted); };

  return (
    <div className="relative flex flex-col items-center" data-testid="piggy-stage" data-played={played} data-animating={flying ? "1" : "0"}>
      <div className="pointer-events-none absolute inset-x-0 -top-2 z-10 flex justify-center">
        <AnimatePresence mode="popLayout">
          {toast && (
            <motion.div key={toast.id} initial={{ opacity: 0, y: 12, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10 }}
              className="max-w-[90%] truncate rounded-full px-4 py-2 text-sm font-extrabold shadow-lg"
              style={{ background: toast.credit ? "rgb(var(--glow-money))" : "rgb(var(--q-text))", color: "rgb(var(--q-bg))" }} data-testid="piggy-toast" data-kind={toast.credit ? "credit" : "debit"}>
              {toast.text}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="flex w-full justify-center pt-8">
        <GlassPiggy balance={bal} count={count} tint={bank.style.tint} accessory={bank.style.accessory} flying={flying} wiggle={wiggle} size={size} onTap={tap} />
      </div>
      <motion.p key={bal} initial={{ scale: reduce ? 1 : 1.12, opacity: 0.6 }} animate={{ scale: 1, opacity: 1 }}
        className={`font-display font-extrabold tracking-tight text-q-text ${size === "lg" ? "text-6xl" : "text-4xl"}`} data-testid="piggy-balance">{money(bal)}</motion.p>
      {controls && (
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <button className="q-btn q-btn-secondary min-h-[48px] px-4" onClick={() => update({ sound_muted: !muted })} aria-pressed={muted} data-testid="piggy-mute-btn" data-muted={muted ? "1" : "0"}>
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />} {muted ? "Sound off" : "Sound on"}
          </button>
          <button className="q-btn q-btn-secondary min-h-[48px] px-4" onClick={() => setStyling(true)} data-testid="piggy-style-btn"><Palette className="h-5 w-5" /> Style my piggy</button>
        </div>
      )}
      {styling && <StylePiggy hubId={hubId} kidId={kidId} style={bank.style} balance={bank.balance} onClose={() => setStyling(false)} onSaved={(style) => { setBank({ ...bank, style }); setStyling(false); }} />}
      {celebration}
    </div>
  );
}
